import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listMyBulkEnquiries,
  USE_API_MEAL_PLANS,
} from '../../api/mealPlansApi';
import { clearStoredAuthIfApiError, readStoredAuth } from './profileFormUtils';
import AccountSessionError from './AccountSessionError';
import AccountBulkOrderDetailsModal from './AccountBulkOrderDetailsModal';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import './AccountLayout.css';

const BULK_TABS = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'received', label: 'Received' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'past', label: 'Past' },
];

function formatDate(value) {
  return formatDisplayDate(value, '—');
}

function formatDateTime(value) {
  return formatDisplayDateTime(value, '—');
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function eventDateIso(enquiry) {
  const raw = enquiry?.payload?.eventDate || '';
  return String(raw).slice(0, 10);
}

function statusBucket(enquiry) {
  const status = String(enquiry?.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled') return 'cancelled';
  if (status === 'completed' || status === 'closed') return 'past';

  const eventDate = eventDateIso(enquiry);
  if (eventDate && eventDate < todayIso()) return 'past';

  if (status === 'received' || status === 'pending' || !status) return 'received';
  if (status === 'confirmed' || status === 'approved' || status === 'in_progress') return 'upcoming';
  if (eventDate && eventDate >= todayIso()) return 'upcoming';
  return 'received';
}

function statusLabel(enquiry) {
  const status = String(enquiry?.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled') return 'Cancelled';
  if (status === 'completed' || status === 'closed') return 'Completed';
  if (status === 'confirmed' || status === 'approved') return 'Confirmed';
  if (status === 'in_progress') return 'In progress';

  const eventDate = eventDateIso(enquiry);
  if (eventDate && eventDate < todayIso()) return 'Past event';
  if (status === 'received' || status === 'pending' || !status) return 'Received';
  return enquiry?.status || '—';
}

function statusTone(enquiry) {
  const bucket = statusBucket(enquiry);
  if (bucket === 'cancelled') return 'cancelled';
  if (bucket === 'past') return 'completed';
  if (bucket === 'upcoming') return 'active';
  return 'pending';
}

function menuLabel(payload = {}) {
  const menu = payload.menuDetails || {};
  if (menu.type === 'custom') return 'Custom menu';
  if (menu.packageName) return menu.packageName;
  return 'Bulk catering';
}

export default function AccountBulkOrders() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionError, setSessionError] = useState(false);
  const [detailsEnquiry, setDetailsEnquiry] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSessionError(false);
    try {
      if (!USE_API_MEAL_PLANS) {
        setError('Bulk order listing requires the DigiRasa API meal-plans feature.');
        setRows([]);
        return;
      }
      const { token } = readStoredAuth();
      if (!token) {
        setSessionError(true);
        return;
      }
      const data = await listMyBulkEnquiries();
      setRows(data?.enquiries || []);
    } catch (err) {
      if (clearStoredAuthIfApiError(err) || err?.status === 401) {
        setSessionError(true);
      } else {
        setError(err?.message || 'Unable to load bulk orders.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const next = {
      all: rows.length,
      upcoming: 0,
      received: 0,
      cancelled: 0,
      past: 0,
    };
    rows.forEach((enquiry) => {
      const bucket = statusBucket(enquiry);
      if (bucket === 'upcoming') next.upcoming += 1;
      if (bucket === 'received') next.received += 1;
      if (bucket === 'cancelled') next.cancelled += 1;
      if (bucket === 'past') next.past += 1;
    });
    return next;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (tab === 'all') return rows;
    return rows.filter((enquiry) => statusBucket(enquiry) === tab);
  }, [rows, tab]);

  if (sessionError) {
    return <AccountSessionError title="Bulk Orders" />;
  }

  return (
    <div className="account-panel">
      <header className="account-panel__header">
        <h1>Bulk Orders</h1>
        <p>Track your catering / event bulk order requests and lookup codes.</p>
      </header>

      <div className="account-sub-tabs" role="tablist" aria-label="Bulk order filters">
        {BULK_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`account-sub-tab${tab === t.id ? ' account-sub-tab--selected' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span>{t.label}</span>
            <span className="account-sub-tab__badge">{counts[t.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading && <p>Loading…</p>}
      {error && <p className="account-form__error" role="alert">{error}</p>}

      {!loading && rows.length === 0 && (
        <p>
          No bulk orders yet.{' '}
          <Link to="/bulk-order">Place a bulk order</Link>.
        </p>
      )}

      {!loading && rows.length > 0 && filteredRows.length === 0 && (
        <p className="account-sub-empty-tab">No {tab} bulk orders.</p>
      )}

      <ul className="account-sub-list">
        {filteredRows.map((enquiry) => {
          const payload = enquiry.payload || {};
          const referenceId = payload.referenceId || enquiry.lookupCode || `ID ${enquiry.id}`;
          const eventType = payload.eventType === 'Other'
            ? `Other — ${payload.otherEventType || ''}`.trim()
            : (payload.eventType || 'Event');
          return (
            <li key={enquiry.id} className="account-sub-card account-sub-card--list">
              <div className="account-sub-card__head">
                <div className="account-sub-card__identity">
                  <div className="account-sub-card__title-row">
                    <strong className="account-sub-card__number">{referenceId}</strong>
                    <span className={`account-sub-status account-sub-status--${statusTone(enquiry)}`}>
                      {statusLabel(enquiry)}
                    </span>
                  </div>
                  <p className="account-sub-card__plan">
                    <span>{eventType}</span>
                    <span className="account-sub-card__dot">{menuLabel(payload)}</span>
                  </p>
                  <p className="account-sub-card__slots">
                    <span className="account-sub-card__slots-label">Tracking</span>
                    {enquiry.lookupCode || '—'}
                  </p>
                </div>
                <div className="account-sub-card__actions">
                  <button
                    type="button"
                    className="account-sub-card__details-btn"
                    onClick={() => setDetailsEnquiry(enquiry)}
                  >
                    View details
                  </button>
                </div>
              </div>

              <div className="account-sub-card__stats" aria-label="Bulk order summary">
                <div>
                  <span>Event date</span>
                  <strong>{formatDate(payload.eventDate)}</strong>
                </div>
                <div>
                  <span>Guests</span>
                  <strong>{payload.guests || '—'}</strong>
                </div>
                <div>
                  <span>Service</span>
                  <strong>{payload.serviceType || '—'}</strong>
                </div>
                <div>
                  <span>Submitted</span>
                  <strong>{formatDateTime(enquiry.createdAt)}</strong>
                </div>
                <div>
                  <span>City</span>
                  <strong>{payload.city || '—'}</strong>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {detailsEnquiry ? (
        <AccountBulkOrderDetailsModal
          enquiry={detailsEnquiry}
          onClose={() => setDetailsEnquiry(null)}
        />
      ) : null}
    </div>
  );
}
